import classnames from "classnames";
import { ReactNode, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";

import type { ParameterTimeSeriesConfiguration } from "@/lib/api";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import Button from "@/components/button/button";
import Icon from "@/components/icon/icon";
import { useTranslation } from "@/lib/i18n";

import type { ObservationsMapDisplayGroupFormEntry } from "./display-group-form.utils";
import { MarkerBubblePreview } from "./marker-bubble-preview";
import {
  deriveDisplayGroupLabelFromParameters,
  isDisplayGroupLabelOverridden,
  resolveDisplayGroupLabel,
  type TimeSeriesParameterEntry,
} from "./display-group-board-sync";
import boardStyles from "./display-group-board.module.css";
import styles from "./display-group-board-item.module.css";
import { DisplayGroupParametersPopover } from "./display-group-parameters-popover";
import {
  isPoolDragItem,
  DISPLAY_GROUP_BOARD_ITEM_TYPE,
  DISPLAY_GROUP_POOL_PARAMETER_TYPE,
  type DisplayGroupBoardDragItem,
  type DisplayGroupPoolDragItem,
} from "./display-group-drag-types";
import { isParameterInTimeSeriesConfigurations } from "./marker-time-series-sync";
import { MarkerParameterTimeSeriesWarning } from "./marker-parameter-time-series-warning";

type BoardDragItem = DisplayGroupBoardDragItem | DisplayGroupPoolDragItem;

function allSourceParametersAreInTarget(
  source: ObservationsMapDisplayGroupFormEntry,
  target: ObservationsMapDisplayGroupFormEntry,
): boolean {
  return source.parameter_ids.every((id) => target.parameter_ids.includes(id));
}

type DisplayGroupBoardItemProps = {
  index: number;
  group: ObservationsMapDisplayGroupFormEntry;
  groups: ObservationsMapDisplayGroupFormEntry[];
  parameters: TimeSeriesParameterOption[];
  poolEntries: TimeSeriesParameterEntry[];
  timeSeriesEntries: TimeSeriesParameterEntry[];
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  catalogLabel: (parameterId: string) => string | undefined;
  onFixParameter: (parameterId: string) => void;
  isParametersPanelOpen: boolean;
  onParametersPanelOpenChange: (open: boolean) => void;
  onReorderParameters: (fromIndex: number, toIndex: number) => void;
  onRemoveParameter: (parameterId: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onCombine: (fromIndex: number, toIndex: number) => void;
  onRename: (index: number, labelOverride: string | null) => void;
  onJoinFromPool: (groupIndex: number, parameterId: string) => void;
  onInsertFromPool: (parameterId: string, atIndex: number) => void;
  onRemove: (index: number) => void;
};

function resolveSingleParameterLabel(
  parameterId: string,
  poolEntries: TimeSeriesParameterEntry[],
  parameters: TimeSeriesParameterOption[],
): string {
  const entry = poolEntries.find((e) => e.parameterId === parameterId);
  const catalog = parameters.find((p) => p.id === parameterId);
  return entry?.label.trim() || catalog?.label || parameterId;
}

function DisplayGroupBoardItem({
  index,
  group,
  groups,
  parameters,
  poolEntries,
  timeSeriesEntries,
  timeSeriesConfigurations,
  catalogLabel,
  onFixParameter,
  isParametersPanelOpen,
  onParametersPanelOpenChange,
  onReorderParameters,
  onRemoveParameter,
  onReorder,
  onCombine,
  onRename,
  onJoinFromPool,
  onRemove,
}: DisplayGroupBoardItemProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const resolvedLabel = resolveDisplayGroupLabel(group, timeSeriesEntries, catalogLabel);
  const labelIsOverridden = isDisplayGroupLabelOverridden(group);
  const [draftName, setDraftName] = useState(group.label_override?.trim() || resolvedLabel);

  const [{ isDragging }, drag] = useDrag({
    type: DISPLAY_GROUP_BOARD_ITEM_TYPE,
    item: (): DisplayGroupBoardDragItem => ({ index }),
    canDrag: () => !isParametersPanelOpen,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver, isCombineTarget, isPoolOver }, drop] = useDrop({
    accept: [DISPLAY_GROUP_BOARD_ITEM_TYPE, DISPLAY_GROUP_POOL_PARAMETER_TYPE],
    hover(item: BoardDragItem, monitor) {
      if (isPoolDragItem(item) || !ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const rect = ref.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const offsetX = clientOffset.x - rect.left;
      const offsetY = clientOffset.y - rect.top;
      const inCenterX = offsetX > rect.width * 0.2 && offsetX < rect.width * 0.8;
      const inCenterY = offsetY > rect.height * 0.2 && offsetY < rect.height * 0.8;
      if (inCenterX && inCenterY) return;

      const hoverMiddleY = rect.height / 2;
      const hoverClientY = offsetY;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      onReorder(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop(item: BoardDragItem, monitor) {
      if (isPoolDragItem(item)) {
        onJoinFromPool(index, item.parameterId);
        return;
      }
      if (item.index === index) return;

      const source = groups[item.index];
      if (!source || allSourceParametersAreInTarget(source, group)) return;

      const clientOffset = monitor.getClientOffset();
      if (!ref.current || !clientOffset) {
        onCombine(item.index, index);
        return;
      }

      const rect = ref.current.getBoundingClientRect();
      const offsetX = clientOffset.x - rect.left;
      const offsetY = clientOffset.y - rect.top;
      const inCenterX = offsetX > rect.width * 0.2 && offsetX < rect.width * 0.8;
      const inCenterY = offsetY > rect.height * 0.2 && offsetY < rect.height * 0.8;
      if (inCenterX && inCenterY) {
        onCombine(item.index, index);
      }
    },
    collect: (monitor) => {
      if (!monitor.isOver() || !ref.current) {
        return { isOver: false, isCombineTarget: false, isPoolOver: false };
      }

      const item = monitor.getItem<BoardDragItem>();
      if (isPoolDragItem(item)) {
        return { isOver: true, isCombineTarget: false, isPoolOver: true };
      }

      const boardItem = item as DisplayGroupBoardDragItem;
      const source = groups[boardItem.index];
      const canCombine =
        source &&
        boardItem.index !== index &&
        !allSourceParametersAreInTarget(source, group);

      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return { isOver: true, isCombineTarget: false, isPoolOver: false };
      }
      const rect = ref.current.getBoundingClientRect();
      const offsetX = clientOffset.x - rect.left;
      const offsetY = clientOffset.y - rect.top;
      const inCenterX = offsetX > rect.width * 0.2 && offsetX < rect.width * 0.8;
      const inCenterY = offsetY > rect.height * 0.2 && offsetY < rect.height * 0.8;
      const isCombine = canCombine && inCenterX && inCenterY;
      return { isOver: true, isCombineTarget: isCombine, isPoolOver: false };
    },
  });

  drag(drop(ref));

  const commitRename = () => {
    const nextName = draftName.trim();
    const derivedLabel = deriveDisplayGroupLabelFromParameters(
      group.parameter_ids,
      timeSeriesEntries,
      catalogLabel,
    );

    if (!nextName || nextName === derivedLabel) {
      onRename(index, null);
      setDraftName(derivedLabel);
    } else if (nextName !== resolvedLabel) {
      onRename(index, nextName);
    } else {
      setDraftName(group.label_override?.trim() || derivedLabel);
    }
    setIsEditingName(false);
  };

  const parameterNamesLabel = deriveDisplayGroupLabelFromParameters(
    group.parameter_ids,
    timeSeriesEntries,
    catalogLabel,
  );
  const singleParameterLabel =
    group.parameter_ids.length === 1
      ? resolveSingleParameterLabel(group.parameter_ids[0], poolEntries, parameters)
      : null;
  const orphanedParameterIds = group.parameter_ids.filter(
    (parameterId) =>
      !isParameterInTimeSeriesConfigurations(parameterId, timeSeriesConfigurations),
  );

  return (
    <div
      ref={ref}
      className={classnames(styles.tile, {
        [styles.dragging]: isDragging,
        [styles.over]: isOver && !isCombineTarget && !isPoolOver,
        [styles.combineTarget]: isCombineTarget,
        [styles.poolTarget]: isPoolOver,
      })}
    >
      <Button
        type="button"
        variant="ghost"
        className={styles.removeButton}
        aria-label={t("admin.widgets.fields.observations_map.marker_designs.board_remove", {
          name: resolvedLabel,
        })}
        onClick={(event) => {
          event.stopPropagation();
          onRemove(index);
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Icon name="x" size={16} />
      </Button>
      <div className={styles.body}>
        <div className={styles.meta}>
          {isEditingName ? (
            <input
              className={styles.nameInput}
              value={draftName}
              autoFocus
              placeholder={resolvedLabel}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") {
                  setDraftName(group.label_override?.trim() || resolvedLabel);
                  setIsEditingName(false);
                }
              }}
              aria-label={t(
                "admin.widgets.fields.observations_map.marker_designs.name_label",
              )}
            />
          ) : (
            <button
              type="button"
              className={classnames(styles.nameButton, {
                [styles.nameButtonDefault]: !labelIsOverridden,
                [styles.nameButtonOverridden]: labelIsOverridden,
              })}
              onClick={() => {
                setDraftName(group.label_override?.trim() || resolvedLabel);
                setIsEditingName(true);
              }}
            >
              {resolvedLabel || t("admin.widgets.fields.model_map.general.untitled")}
            </button>
          )}
        </div>
        {singleParameterLabel ? (
          <span className={styles.parameterName}>{singleParameterLabel}</span>
        ) : (
          <DisplayGroupParametersPopover
            open={isParametersPanelOpen}
            onOpenChange={onParametersPanelOpenChange}
            parameterCount={group.parameter_ids.length}
            parameterNamesLabel={parameterNamesLabel}
            parameterIds={group.parameter_ids}
            poolEntries={poolEntries}
            parameters={parameters}
            onReorder={onReorderParameters}
            onRemove={onRemoveParameter}
          />
        )}
        {orphanedParameterIds.length > 0 && (
          <div className={styles.warnings}>
            {orphanedParameterIds.map((parameterId) => (
              <MarkerParameterTimeSeriesWarning
                key={parameterId}
                onFix={() => onFixParameter(parameterId)}
              />
            ))}
          </div>
        )}
      </div>
      <div className={styles.preview}>
        <MarkerBubblePreview
          parameters={parameters}
          parameterIds={group.parameter_ids}
          timeSeriesConfigurations={timeSeriesConfigurations}
        />
      </div>
    </div>
  );
}

type DisplayGroupBoardDropZoneProps = {
  atIndex: number;
  onInsertFromPool: (parameterId: string, atIndex: number) => void;
  className?: string;
  children?: ReactNode;
};

function DisplayGroupBoardDropZone({
  atIndex,
  onInsertFromPool,
  className,
  children,
}: DisplayGroupBoardDropZoneProps) {
  const [{ isOver }, drop] = useDrop({
    accept: DISPLAY_GROUP_POOL_PARAMETER_TYPE,
    drop(item: DisplayGroupPoolDragItem) {
      onInsertFromPool(item.parameterId, atIndex);
    },
    collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
  });

  return (
    <div
      ref={drop}
      className={classnames(className, {
        [boardStyles.appendDropOver]: isOver,
        [boardStyles.emptyOver]: isOver && className === boardStyles.empty,
      })}
    >
      {children}
    </div>
  );
}

type DisplayGroupBoardProps = {
  groups: ObservationsMapDisplayGroupFormEntry[];
  parameters: TimeSeriesParameterOption[];
  poolEntries: TimeSeriesParameterEntry[];
  timeSeriesEntries: TimeSeriesParameterEntry[];
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  catalogLabel: (parameterId: string) => string | undefined;
  onFixParameter: (parameterId: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onCombine: (fromIndex: number, toIndex: number) => void;
  onRename: (index: number, labelOverride: string | null) => void;
  onJoinFromPool: (groupIndex: number, parameterId: string) => void;
  onInsertFromPool: (parameterId: string, atIndex: number) => void;
  onRemove: (index: number) => void;
  parametersPanelGroupId: string | null;
  onParametersPanelOpenChange: (groupId: string, open: boolean) => void;
  onReorderParameters: (groupId: string, fromIndex: number, toIndex: number) => void;
  onRemoveParameter: (groupId: string, parameterId: string) => void;
  emptyState?: ReactNode;
};

export function DisplayGroupBoard({
  groups,
  parameters,
  poolEntries,
  timeSeriesEntries,
  timeSeriesConfigurations,
  catalogLabel,
  onFixParameter,
  onReorder,
  onCombine,
  onRename,
  onJoinFromPool,
  onInsertFromPool,
  onRemove,
  parametersPanelGroupId,
  onParametersPanelOpenChange,
  onReorderParameters,
  onRemoveParameter,
  emptyState,
}: DisplayGroupBoardProps) {
  if (groups.length === 0) {
    return emptyState ? (
      <DisplayGroupBoardDropZone
        atIndex={0}
        onInsertFromPool={onInsertFromPool}
        className={boardStyles.empty}
      >
        {emptyState}
      </DisplayGroupBoardDropZone>
    ) : null;
  }

  return (
    <div className={boardStyles.board}>
      {groups.map((group, index) => (
        <DisplayGroupBoardItem
          key={group.id}
          index={index}
          group={group}
          groups={groups}
          parameters={parameters}
          poolEntries={poolEntries}
          timeSeriesEntries={timeSeriesEntries}
          timeSeriesConfigurations={timeSeriesConfigurations}
          catalogLabel={catalogLabel}
          onFixParameter={onFixParameter}
          isParametersPanelOpen={parametersPanelGroupId === group.id}
          onParametersPanelOpenChange={(open) =>
            onParametersPanelOpenChange(group.id, open)
          }
          onReorderParameters={(fromIndex, toIndex) =>
            onReorderParameters(group.id, fromIndex, toIndex)
          }
          onRemoveParameter={(parameterId) => onRemoveParameter(group.id, parameterId)}
          onReorder={onReorder}
          onCombine={onCombine}
          onRename={onRename}
          onJoinFromPool={onJoinFromPool}
          onInsertFromPool={onInsertFromPool}
          onRemove={onRemove}
        />
      ))}
      <DisplayGroupBoardDropZone
        atIndex={groups.length}
        onInsertFromPool={onInsertFromPool}
        className={boardStyles.appendDrop}
      />
    </div>
  );
}
